import { Button, LoadSpinner } from '@calizahq/react-components-v2'
import { Form, Formik } from 'formik'
import React from 'react'
import * as Yup from 'yup'

import { InputSelect } from '@/components'
import { useAlertContext } from '@/context'
import { Options } from '@/forms/CreateTicket/CreateTicket.util'
import { TicketMutations } from '@/graphql/mutations/ticket.mutations'
import { useMobile } from '@/hooks'
import { useGraphqlMutation } from '@/hooks/useGraphql'
import useTicketReasons from '@/hooks/useTicketReasons/useTicketReasons'
import useTicketTypes from '@/hooks/useTicketTypes/useTicketTypes'

type Props = {
  refetchDetails: () => void
  ticketId: number
  ticketType?: string
  ticketTypeId: number
  toggle: () => void
}

export const TypeSelection = ({ ticketTypeId, ticketType, toggle, ticketId, refetchDetails }: Props) => {
  const { setAlert } = useAlertContext()

  const { data: typesData, isPending: isTicketTypeLoading } = useTicketTypes()
  const { data: reasonsData, isPending: isTicketReasonsLoading } = useTicketReasons()

  const { mutate, isPending: isMutationLoading } = useGraphqlMutation(TicketMutations.updateTicket(), {
    onSuccess: () => {
      toggle()
      refetchDetails()
      setAlert('Type updated', 'success')
    },
  })

  const { isTablet } = useMobile()
  const ticketTypeOptions: Options =
    typesData && typesData.ticketTypes
      ? typesData.ticketTypes.map((type) => {
          return { label: type.description, value: type.id }
        })
      : []

  ticketTypeOptions.unshift({ label: 'Select Type', value: '' })

  let ticketReasonsOptions: Options = [{ label: 'Select Reason', value: '' }]
  const ticketReasons = reasonsData && reasonsData.ticketReasons ? reasonsData.ticketReasons : []

  const isLoading = isTicketTypeLoading || isTicketReasonsLoading

  const handleSubmit = (values: FormValues) => {
    mutate({
      ticket: {
        ...values,
        ticketId,
      },
    })
  }

  return (
    <>
      {isLoading ? (
        <div className="flex h-48 w-full items-center justify-center ">
          <LoadSpinner className="icon-secondary" isCurrentColor={true} size={2} />
        </div>
      ) : (
        <Formik
          enableReinitialize={true}
          initialValues={{
            ticketTypeId,
          }}
          onSubmit={handleSubmit}
          validateOnBlur={false}
          validationSchema={formSchema}
        >
          {({ setFieldValue }) => {
            function handleTypeChange(val: any) {
              setFieldValue('ticketReasonId', undefined)

              ticketReasonsOptions = ticketReasons
                .filter((reason) => reason.ticketTypeId === val)
                .map((reason) => {
                  return { label: reason.description, value: reason.id }
                })
              ticketReasonsOptions.unshift({ label: 'Select Reason', value: '' })
            }

            return (
              <Form className="flex w-full flex-col flex-wrap px-6 md:max-w-120">
                <div className="mb-16 flex w-full flex-col md:grid md:grid-cols-1 md:gap-4">
                  <InputSelect
                    className=" mb-4 w-full md:mb-0 md:mr-4"
                    id="ticketTypeId"
                    label="Type"
                    name="ticketTypeId"
                    onChange={handleTypeChange}
                    options={ticketTypeOptions}
                  ></InputSelect>
                  <InputSelect
                    className="mb-4 w-full md:mb-0"
                    disabled={!ticketType || ticketType === ''}
                    id="ticketReasonId"
                    label="Reason"
                    name="ticketReasonId"
                    options={ticketReasonsOptions}
                  ></InputSelect>
                </div>

                <div className="flex flex-col-reverse justify-between md:flex-row">
                  <Button
                    className="w-full bg-action-secondary md:flex md:w-auto"
                    onClick={toggle}
                    size={isTablet ? 'medium' : 'small'}
                    variant="secondary"
                  >
                    Close
                  </Button>
                  <Button
                    className="mb-2 w-full bg-text-semantic-success md:mb-0 md:w-auto"
                    disabled={isMutationLoading}
                    isLoading={isMutationLoading}
                    size={isTablet ? 'medium' : 'small'}
                    type="submit"
                  >
                    Update Type
                  </Button>
                </div>
              </Form>
            )
          }}
        </Formik>
      )}
    </>
  )
}

const formSchema = Yup.object({
  ticketTypeId: Yup.string().required('Please select Ticket Type'),
  ticketReasonId: Yup.string().required('Please select Ticket Reason'),
})

interface FormValues {
  ticketReasonId?: number
  ticketTypeId?: number
}
